import * as React from 'react';
import { Input } from '../Input/Input';
import { useLatest } from '../helper/hooks';

export interface DateInputProps {
  id?: string;
  value?: string;
  /**
   * callback to get the selected value in format of DD-MM-YYYY
   */
  onChangeValue?: (value: string) => void;
  onFocus?: (event: CustomEvent) => void;
  onBlur?: (event: CustomEvent) => void;
  /**
   * callback when date picker is closed, either by select a date or click other element
   */
  onClose?: () => void;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  /**
   * state if the calendar month can be changed.
   * @default true
   */
  allowChangeMonth?: boolean;
  /**
   * minimum date in format of DD-MM-YYYY
   */
  minDate?: string;
  /**
   * maximum date in format of DD-MM-YYYY
   */
  maxDate?: string;
  /**
   * limit the date options to weekdays only (Monday to Friday)
   */
  weekdayOnly?: boolean;
  /**
   * a list of disabled dates in format of DD-MM-YYYY
   */
  disabledDates?: string[];
}

/**
 * Use a module variable to track because web component trigger load event once. And since web component does not work in server side, don't worry about SSR issue.
 */
let IS_COMPONENT_LOADED = false;

/**
 * `DateInput` is a wrapper over `flos-date-input` web component.
 *
 * We use `flos-date-input` instead of other React library because there is not a good library as far as I know (either it is not powerful enough or it is too heavy).
 * `flos-date-input` supports many features with the underlying JQuery plugin while it is being lazy-loaded in TopDK site!
 *
 * If we find a good React implementation in future, we will refactor this component.
 *
 * **Note**: You probably want to use [`DateField`](#datefield) instead.
 */
export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(function DateInputComponent(
  {
    onChangeValue,
    onFocus,
    onBlur,
    onClose,
    disabled,
    id,
    allowChangeMonth = true,
    minDate,
    maxDate,
    weekdayOnly,
    disabledDates,
    required,
    readonly,
    ...props
  },
  forwardedRef
) {
  const ref = React.useRef<HTMLElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [loaded, setLoaded] = React.useState(IS_COMPONENT_LOADED);

  const onChangeValueRef = useLatest(onChangeValue);
  const onBlurRef = useLatest(onBlur);
  const onCloseRef = useLatest(onClose);

  const focusTimer = React.useRef<number | null>(null);
  const onFocusRef = useLatest(function handleFocus(ev: CustomEvent) {
    if (onFocus) {
      onFocus(ev);
    }
    if (inputRef.current) {
      // HACK: use jQuery to open the datepicker because when we focus programatically the input, the datepicker is not opened (don't know why)
      const datepicker = (window as any).jQuery(inputRef.current);
      focusTimer.current = window.setTimeout(() => {
        datepicker.datepick('show');
      }, 400);
    }
  });
  React.useEffect(() => {
    return () => {
      if (focusTimer.current) {
        window.clearTimeout(focusTimer.current);
      }
    };
  }, []);

  const handleEvent = React.useCallback(
    (event: CustomEvent) => {
      switch (event.detail.event) {
        case 'inputRef':
          // We have to manage ref manually as React doesn't provide an elegant way to merge them.
          // See https://github.com/facebook/react/issues/13029
          inputRef.current = event.detail.value as HTMLInputElement;
          if (forwardedRef) {
            if (typeof forwardedRef === 'function') {
              forwardedRef(event.detail.value as HTMLInputElement);
            } else if (forwardedRef !== null) {
              (forwardedRef as any).current = event.detail.value as HTMLInputElement;
            }
          }
          setLoaded(true);
          IS_COMPONENT_LOADED = true;
          break;

        case 'onChange':
          if (onChangeValueRef.current) {
            onChangeValueRef.current(event.detail.value);
          }
          break;

        case 'onFocus':
          if (onFocusRef.current) {
            onFocusRef.current(event);
          }
          break;

        case 'onBlur':
          if (onBlurRef.current) {
            onBlurRef.current(event);
          }
          break;

        case 'onClose':
          if (onCloseRef.current) {
            onCloseRef.current();
          }
          break;

        default:
          break;
      }
    },
    [forwardedRef, onChangeValueRef, onFocusRef, onBlurRef, onCloseRef]
  );

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    if (ref.current.className === 'hydrated') {
      // sometimes component already loaded before useEffect is run
      setLoaded(true);
    }

    const flosDateComponentRef = ref.current;
    const callback = handleEvent;

    flosDateComponentRef.addEventListener('fieldEvent', callback);

    return () => {
      flosDateComponentRef.removeEventListener('fieldEvent', callback);
    };
  }, [handleEvent]);

  const disabledDatesValue = disabledDates ? JSON.stringify(disabledDates) : undefined;

  return (
    <>
      <flos-date-input
        input-id={id}
        disabled={disabled ? true : undefined}
        required={required ? true : undefined}
        change-month={allowChangeMonth ? true : undefined}
        min-date={minDate}
        max-date={maxDate}
        no-weekend={weekdayOnly ? true : undefined}
        disabled-dates={disabledDatesValue}
        read-only={readonly ? true : undefined}
        {...props}
        ref={ref}
      />
      {/* render a disabled input while the web components is being loaded to minimize layout jump */}
      {!loaded && (
        <div className="date-input">
          <Input id={id} required={required} readOnly={true} />
        </div>
      )}
    </>
  );
});
